import { type ComponentPropsWithoutRef, forwardRef, type ReactNode, useId } from 'react';

import { cn } from '@/utils/cn';

import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ComponentPropsWithoutRef<'button'> {
  variant?: ButtonVariant | undefined;
  size?: ButtonSize | undefined;
  /**
   * Disables the button, renders a spinner, and sets `aria-busy`. Mandatory rather than a caller
   * concern: every mutation in this app must be single-flight (invariant I-6), and making the busy
   * state part of the primitive means a developer cannot forget it.
   */
  isLoading?: boolean | undefined;
  /** Localized name for the spinner. Required whenever `isLoading` can become true. */
  loadingLabel?: string | undefined;
  startIcon?: ReactNode | undefined;
  endIcon?: ReactNode | undefined;
  fullWidth?: boolean | undefined;
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-on-primary hover:bg-primary-hover active:bg-primary-active',
  secondary: 'bg-secondary-soft text-secondary-fg hover:bg-surface',
  ghost: 'bg-transparent text-text hover:bg-surface-muted',
  danger: 'bg-danger text-on-primary hover:opacity-90 active:opacity-80',
};

const SIZES: Record<ButtonSize, string> = {
  // `sm` keeps a 44px hit area via padding even though the visual box is shorter.
  sm: 'min-h-11 px-3 text-sm',
  md: 'min-h-11 px-4 text-base',
  lg: 'min-h-13 px-5 text-base',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    isLoading = false,
    loadingLabel,
    startIcon,
    endIcon,
    fullWidth = false,
    className,
    disabled,
    type = 'button',
    children,
    ...rest
  },
  ref,
) {
  const nameId = useId();

  return (
    <button
      ref={ref}
      // Explicit: an unspecified `type` inside a form is `submit`, which has submitted more
      // payments by accident than any other default in HTML.
      type={type}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-md font-semibold',
        'transition-colors duration-150 motion-reduce:transition-none',
        'focus-visible:ring-focus-ring focus-visible:ring-2 focus-visible:ring-offset-2',
        'focus-visible:ring-offset-background outline-none',
        'disabled:cursor-not-allowed disabled:opacity-60',
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
      disabled={disabled === true || isLoading}
      aria-busy={isLoading || undefined}
      // Name is the children span only. The spinner's `role="status"` would otherwise join the
      // accessible name, so a loading submit would be announced as "Submitting Submit confirmation".
      aria-labelledby={nameId}
    >
      {/*
       * The accessible name does not change while loading. A label that flips to "Loading…" makes
       * screen-reader users lose their place, and on a submit button it also loses the only
       * confirmation of what they are submitting.
       */}
      {isLoading && loadingLabel !== undefined ? (
        <Spinner label={loadingLabel} size="sm" />
      ) : (
        startIcon !== undefined && (
          <span aria-hidden="true" className="inline-flex shrink-0">
            {startIcon}
          </span>
        )
      )}
      <span id={nameId} className="truncate">
        {children}
      </span>
      {!isLoading && endIcon !== undefined && (
        <span aria-hidden="true" className="inline-flex shrink-0">
          {endIcon}
        </span>
      )}
    </button>
  );
});
